import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useUI from 'src/hooks/use-ui';
import { useAddToCartModal } from 'components/modals/add-to-cart';
import { tracker } from 'src/utils/analytics';

import { useSponsoredProductHandlers } from 'src/utils/ads/hooks/sponsored-products';
import { SponsoredBrandCarouselV2 } from 'src/components/carousels/ads-carousels/sponsored-brand-carousel-v2';
import { CategoryPageRecommendations } from 'src/components/carousels/personalization/category-page-recommendations';
import FullCard from 'src/components/product-card-v2/full-card';
import ProductListItem from './product-list-item';
import { useGroupProductsByRows } from './use-group-products-by-rows';
import { getShowInterstitialCarousels } from './product-list.utils';

export const ROWS_FOR_ADS = [3, 5];

export default function ProductItemList({
  products,
  dispensary,
  trackerSource,
  adPlacement,
  productsCarouselQueries,
  hasPagination = false,
}) {
  const flags = useFlags();
  const isProductCardsV2Enabled = flags['growth.ecomm.product-card-v2.rollout'] ?? false;

  const { showSponsoredBrandsCarousel, showCategoryRecommendationsCarousel } = getShowInterstitialCarousels(
    productsCarouselQueries
  );
  const { sponsoredBrandQuery, personalizedProductsQuery } = productsCarouselQueries ?? {
    sponsoredBrandQuery: null,
    personalizedProductsQuery: null,
  };

  const UI = useUI();
  const { open: openAddToCartModal } = useAddToCartModal();

  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up(`md`));

  const { getProductsGroup } = useGroupProductsByRows({
    products,
    isListView: true,
    rows: ROWS_FOR_ADS,
  });

  const { handleSponsoredProductClick, handleSponsoredProductImpression } = useSponsoredProductHandlers({
    trackerSource,
    adPlacement,
  });

  const handleClick = useCallback(
    (product, index) => {
      tracker.setContext({ activeProductPosition: index });
      tracker.productClicked({
        product,
        position: index,
        trackerSource,
        placement: 'menu-page-product-item-list',
      });

      UI.activeProductSource = trackerSource;
      UI.setProductPosition({ productId: product.id, productIndex: index });

      handleSponsoredProductClick(product, index);
    },
    [UI, trackerSource, handleSponsoredProductClick]
  );

  const handleAddToCart = useCallback(
    (product, index, option) => {
      openAddToCartModal({
        quantity: 1,
        option,
        product,
        dispensary,
        trackerSource,
      });

      handleClick(product, index);
    },
    [dispensary, handleClick, openAddToCartModal, trackerSource]
  );

  const ProductItemComponent = isProductCardsV2Enabled ? FullCard : ProductListItem;

  const ProductItem = ({ product, index, ...props }) => (
    <Wrapper data-testid='product-list-item' key={product.id}>
      <ProductItemComponent
        {...props}
        product={product}
        productIndex={index}
        onClick={handleClick}
        onView={handleSponsoredProductImpression}
        onAddToCart={handleAddToCart}
        isMobile={!isDesktop}
      />
    </Wrapper>
  );

  return (
    <Wrapper data-testid='product-list' $includeBottomBorder={isProductCardsV2Enabled && hasPagination}>
      {getProductsGroup(0).map(([product, globalIndex]) => (
        <ProductItem key={product.id} product={product} index={globalIndex} />
      ))}

      {showSponsoredBrandsCarousel && (
        <AdUnit data-testid='ad-unit-sponsored-brands-carousel' $includeTopMargin={!isProductCardsV2Enabled}>
          <SponsoredBrandCarouselV2 sponsoredBrandQuery={sponsoredBrandQuery} adPlacement='category.carousel' />
        </AdUnit>
      )}

      {getProductsGroup(1).map(([product, globalIndex], index) => (
        <ProductItem
          key={product.id}
          product={product}
          index={globalIndex}
          hideBorder={index === 0 && showSponsoredBrandsCarousel}
        />
      ))}

      {showCategoryRecommendationsCarousel && (
        <AdUnit data-testid='ad-unit-category-recommendations-carousel' $includeTopMargin={!isProductCardsV2Enabled}>
          <CategoryPageRecommendations personalizedProductsQuery={personalizedProductsQuery} />
        </AdUnit>
      )}

      {getProductsGroup(2).map(([product, globalIndex], index) => (
        <ProductItem
          key={product.id}
          product={product}
          index={globalIndex}
          hideBorder={index === 0 && showCategoryRecommendationsCarousel}
        />
      ))}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  ${({ $includeBottomBorder }) =>
    $includeBottomBorder &&
    css`
      border-bottom: 1px solid #e3e7e9;
    `}
`;

const AdUnit = styled.aside`
  @media (min-width: 960px) and (max-width: 1240px) {
    width: var(--kiosk-pro-content-width, calc(100vw - 270px));
  }

  &:last-child {
    border-top: 1px solid ${({ theme }) => theme.colors.blueGrey[90]};

    ${({ $includeTopMargin }) =>
      $includeTopMargin &&
      css`
        margin-top: 24px;
      `}
  }

  & > div {
    padding: 0;

    ${({ $includeTopMargin }) =>
      $includeTopMargin &&
      css`
        margin-top: 24px;
      `}
  }
`;
